import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { EventContent } from "@tih/shared";
import { ComingSoonDirections } from "../components/coming-soon/ComingSoonDirections";
import { ComingSoonEventGrid } from "../components/coming-soon/ComingSoonEventGrid";
import { ComingSoonExplore } from "../components/coming-soon/ComingSoonExplore";
import { ComingSoonEyebrow } from "../components/coming-soon/ComingSoonEyebrow";
import { EventCancellationBanner } from "../components/EventCancellationBanner";
import { SiteAnnouncementBanner } from "../components/SiteAnnouncementBanner";
import { ComingSoonReplay } from "../components/coming-soon/ComingSoonReplay";
import { SiteAtmosphere } from "../components/SiteAtmosphere";
import { FooterClouds } from "../components/FooterClouds";
import { useCountdown } from "../hooks/useCountdown";
import { useSiteCopy } from "../context/SiteCopyContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useAuth } from "../context/AuthContext";
import { useLiveInfo } from "../hooks/useLiveInfo";
import {
  applyHomepageVideoToLiveInfo,
  homepageVideoFromCopy,
  shouldShowHomepageVideo,
} from "../lib/homepage-video";
import { isBroadcastLive } from "../lib/live-display";

type Props = {
  eventPayload: EventContent | null;
};

export function ComingSoonPage({ eventPayload }: Props) {
  const { settings } = useSiteSettings();
  const { copy } = useSiteCopy();
  const { user, isAdmin } = useAuth();
  const liveInfo = useLiveInfo();
  const homepageVideo = homepageVideoFromCopy(copy);
  const displayLiveInfo = applyHomepageVideoToLiveInfo(liveInfo, homepageVideo);

  const hasScheduledEvent = settings.countdownSource === "event";

  const target = useMemo(
    () => Date.parse(settings.launchAt),
    [settings.launchAt],
  );
  const { done } = useCountdown(target);

  const thanksLine = hasScheduledEvent
    ? undefined
    : "See you at the next worship overnight.";
  const thanksIso = hasScheduledEvent
    ? (eventPayload?.date ?? settings.launchAt)
    : undefined;

  const live = isBroadcastLive(liveInfo);
  const showLiveVideo = shouldShowHomepageVideo(liveInfo, homepageVideo);

  return (
    <section className="coming-soon">
      <SiteAtmosphere />
      <SiteAnnouncementBanner inline />

      <header className="coming-soon-nav">
        <div className="coming-soon-brand">
          <h1 className="coming-soon-brand-title">
            <img
              src="/assets/logo.png"
              alt="Together In Heaven"
              className="brand-logo"
            />
          </h1>
        </div>
        <nav className="coming-soon-nav-links" aria-label="Site">
          <Link to="/dorothy">About us</Link>
          <Link to="/blog">Blog</Link>
          <Link to="/gallery">Gallery</Link>
          <Link to="/videos">Videos</Link>
          <Link to="/participate">Participate</Link>
          <Link to="/give">Give</Link>
        </nav>
        <div className="coming-soon-nav-right">
          {user ? (
            <Link to={isAdmin ? "/admin" : "/"} className="coming-soon-pill">
              {isAdmin ? "Admin dashboard" : "Enter site"}
              <span className="arr">→</span>
            </Link>
          ) : (
            <Link to="/sign-in" className="coming-soon-pill ghost">
              Sign in
            </Link>
          )}
        </div>
      </header>

      <ComingSoonEyebrow isLive={live} />

      {eventPayload?.cancellation && (
        <EventCancellationBanner notice={eventPayload.cancellation} />
      )}

      <ComingSoonEventGrid
        liveInfo={displayLiveInfo}
        eventId={eventPayload?.eventId ?? null}
        eventPhase={eventPayload?.eventPhase}
        dateIso={settings.launchAt}
        posterUrl={eventPayload?.posterUrl}
        posterCaption={eventPayload?.eventTitle}
        showLiveVideo={showLiveVideo}
        showCountdown={hasScheduledEvent && !done}
        showWhen={hasScheduledEvent}
        showRsvp={hasScheduledEvent || !eventPayload?.cancellation}
        showHeroLogo={false}
        showParticipate
        thanksIso={thanksIso}
        thanksLine={thanksLine}
      />

      <ComingSoonDirections venueMap={eventPayload?.venueMap ?? null} />

      <ComingSoonExplore />

      <ComingSoonReplay
        liveInfo={displayLiveInfo}
        showLiveVideo={showLiveVideo}
        posterUrl={eventPayload?.posterUrl}
      />

      <footer className="coming-soon-footer">
        <FooterClouds />
        <div className="coming-soon-footer-links">
          <Link to="/dorothy">About</Link>
          <a href="#poster">Events</a>
          <Link to="/participate">Participate</Link>
          <Link to="/give">Give</Link>
          <Link to="/blog">Blog</Link>
          <Link to="/gallery">Gallery</Link>
          <Link to="/videos">Videos</Link>
        </div>
        <div className="coming-soon-footer-meta">
          <span>© {new Date().getFullYear()} Together In Heaven</span>
          <span className="dot" aria-hidden="true">·</span>
          <span>Kampala, Uganda</span>
        </div>
      </footer>
    </section>
  );
}
